import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

/**
 * Voice conversation helper for chat UIs.
 *
 * - Speech-to-text via the browser SpeechRecognition API (Chrome, Edge,
 *   Safari incl. iOS). Exposes `supported` so UIs can hide the mic where
 *   unavailable (e.g. Firefox).
 * - Text-to-speech via speechSynthesis for reading assistant replies aloud.
 */

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: any) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: any) => void) | null;
};

function getRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === "undefined") return null;
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export interface UseVoiceChatOptions {
  /** Called with the final transcript when the user stops speaking. */
  onTranscript: (text: string) => void;
  /** Called with interim (in-progress) transcript for live display. */
  onInterim?: (text: string) => void;
  /** Called when the microphone cannot be used (e.g. permission denied). */
  onMicError?: (message: string) => void;
}

export function useVoiceChat({ onTranscript, onInterim, onMicError }: UseVoiceChatOptions) {
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceReplies, setVoiceReplies] = useState(false);
  const [conversationMode, setConversationMode] = useState(false);
  const conversationModeRef = useRef(false);
  // Our own speaking flag — window.speechSynthesis.speaking is unreliable
  // in Chrome (can stay stuck true after cancel), which would keep the
  // mic-reopen loop waiting forever.
  const speakingRef = useRef(false);
  // Consecutive "no-speech" endings with zero transcribed audio — used to
  // detect environments where the mic is technically open but silent
  // (e.g. speech recognition inside an embedded preview iframe).
  const silentRunsRef = useRef(0);
  const silenceWarnedRef = useRef(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const premiumAudioRef = useRef<HTMLAudioElement | null>(null);
  const startListeningRef = useRef<(() => void) | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  const onInterimRef = useRef(onInterim);
  const onMicErrorRef = useRef(onMicError);
  onTranscriptRef.current = onTranscript;
  onInterimRef.current = onInterim;
  onMicErrorRef.current = onMicError;

  const sttSupported = getRecognitionCtor() !== null;
  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  // User's chosen Zeus voice (set on the Zeus Settings page).
  const { data: userPrefs } = useQuery<{ zeusVoiceUri?: string | null; zeusVoiceName?: string | null; zeusVoiceRate?: number | null }>({
    queryKey: ["/api/user/preferences"],
  });
  const preferredVoiceRef = useRef<{ uri?: string | null; name?: string | null; rate?: number | null }>({});
  preferredVoiceRef.current = {
    uri: userPrefs?.zeusVoiceUri,
    name: userPrefs?.zeusVoiceName,
    rate: userPrefs?.zeusVoiceRate,
  };

  const setSpeakingState = useCallback((value: boolean) => {
    speakingRef.current = value;
    setSpeaking(value);
  }, []);

  const endConversation = useCallback(() => {
    conversationModeRef.current = false;
    setConversationMode(false);
  }, []);

  const stopListening = useCallback(() => {
    endConversation();
    recognitionRef.current?.stop();
  }, [endConversation]);

  const startListening = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor || recognitionRef.current) return;
    // Stop any ongoing speech so the mic doesn't pick it up. The mic is
    // never open while Zeus talks — on devices with weak echo cancellation
    // it hears his own voice, interrupting playback and transcribing his
    // words as user input.
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    premiumAudioRef.current?.pause();
    premiumAudioRef.current = null;
    setSpeakingState(false);
    const rec = new Ctor();
    rec.lang = navigator.language || "en-US";
    rec.continuous = false;
    rec.interimResults = true;

    let finalText = "";
    let fatalMicError = false;
    let wasAborted = false;
    rec.onresult = (event: any) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      console.info("[voice] result", { finalText, interim });
      silentRunsRef.current = 0;
      silenceWarnedRef.current = false;
      if (interim && onInterimRef.current) onInterimRef.current(finalText + interim);
    };
    rec.onend = () => {
      console.info("[voice] recognition ended", { finalText, fatalMicError, wasAborted });
      if (recognitionRef.current === rec) recognitionRef.current = null;
      setListening(false);
      if (fatalMicError) return;
      const text = finalText.trim();
      if (text) {
        onTranscriptRef.current(text);
      } else if (conversationModeRef.current && !wasAborted) {
        // Note: an "aborted" recognition never auto-reopens here — aborts
        // are always intentional (TTS started, unmount, or another
        // recognition superseding this one) and reopening from here causes
        // dueling-recognition ping-pong loops. After Zeus speaks, the mic
        // is reopened by the TTS finish handlers instead.
        // In hands-free conversation mode the browser may time out on
        // silence; reopen the mic so the conversation stays live.
        let reopenWaitMs = 0;
        const reopenWhenQuiet = () => {
          if (!conversationModeRef.current || recognitionRef.current) return;
          // If Zeus is still talking, wait — opening the mic while he
          // speaks makes it transcribe his own voice as user input. Uses
          // our own speaking flag (browser flags can get stuck true).
          // Fail-safe: after 90s, reopen anyway (startListening clears any
          // stale speech state) so a stuck flag can't kill the conversation.
          const stillSpeaking =
            speakingRef.current ||
            (premiumAudioRef.current !== null && !premiumAudioRef.current.paused);
          if (stillSpeaking && reopenWaitMs < 90_000) {
            reopenWaitMs += 300;
            window.setTimeout(reopenWhenQuiet, 300);
            return;
          }
          startListeningRef.current?.();
        };
        window.setTimeout(reopenWhenQuiet, 300);
      }
    };
    rec.onerror = (event: any) => {
      const err = event?.error ?? "unknown";
      console.warn("[voice] recognition error:", err);
      if (err === "aborted") wasAborted = true;
      if (err === "no-speech") {
        silentRunsRef.current += 1;
        const embedded = typeof window !== "undefined" && window.self !== window.top;
        if (!silenceWarnedRef.current && ((embedded && silentRunsRef.current >= 2) || silentRunsRef.current >= 5)) {
          silenceWarnedRef.current = true;
          onMicErrorRef.current?.(
            embedded
              ? "Voice input doesn't work inside the embedded preview. Open the app in its own browser tab (use the open-in-new-tab button) and try the mic there."
              : "The microphone is open but hears only silence. Check that the right mic is selected in your browser's site settings and that its input volume is up.",
          );
        }
      }
      // onend fires after onerror; just make sure state resets.
      if (recognitionRef.current === rec) recognitionRef.current = null;
      setListening(false);
      if (err === "not-allowed" || err === "service-not-allowed" || err === "audio-capture") {
        // Mic is blocked or missing — retrying is pointless. Stop the
        // hands-free loop and tell the user instead of spinning forever.
        fatalMicError = true;
        endConversation();
        onMicErrorRef.current?.(
          err === "audio-capture"
            ? "No microphone was found. Check that a mic is connected and enabled."
            : "Microphone access is blocked. Allow microphone access for this site in your browser settings, then try again.",
        );
      }
    };

    recognitionRef.current = rec;
    setListening(true);
    try {
      rec.start();
      console.info("[voice] recognition started");
    } catch (e) {
      console.warn("[voice] recognition failed to start:", e);
      recognitionRef.current = null;
      setListening(false);
    }
  }, [endConversation]);
  startListeningRef.current = startListening;

  const toggleListening = useCallback(() => {
    if (recognitionRef.current || conversationModeRef.current) {
      stopListening();
    } else {
      // Entering listening via the mic starts hands-free conversation mode:
      // after Zeus finishes speaking a reply, the mic reopens automatically.
      conversationModeRef.current = true;
      setConversationMode(true);
      startListening();
    }
  }, [startListening, stopListening]);

  /**
   * Speak a reply aloud. If conversation mode is active, automatically
   * resume listening once speech finishes (or immediately when TTS is
   * unavailable), enabling a hands-free back-and-forth.
   */
  const speakText = useCallback(
    (text: string) => {
      const resumeIfConversing = () => {
        if (conversationModeRef.current) startListening();
      };
      if (!text) {
        resumeIfConversing();
        return;
      }

      const { uri: prefUri, rate: prefRate } = preferredVoiceRef.current;
      const clampedRate =
        typeof prefRate === "number" && prefRate > 0 ? Math.min(2, Math.max(0.5, prefRate)) : 1;

      // Premium AI voice: fetch generated audio from the server and play it.
      if (prefUri && prefUri.startsWith("premium:")) {
        const voiceId = prefUri.slice("premium:".length);
        if (ttsSupported) window.speechSynthesis.cancel();
        premiumAudioRef.current?.pause();
        premiumAudioRef.current = null;
        apiRequest("POST", "/api/zeus/tts", { text, voice: voiceId })
          .then(async (res) => {
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const audio = new Audio(objectUrl);
            audio.playbackRate = clampedRate;
            audio.onplay = () => {
              setSpeakingState(true);
              // Keep the mic closed while premium audio plays; it reopens
              // in finish() so Zeus's voice is never picked up as input.
              recognitionRef.current?.abort();
            };
            const finish = () => {
              URL.revokeObjectURL(objectUrl);
              if (premiumAudioRef.current === audio) premiumAudioRef.current = null;
              setSpeakingState(false);
              resumeIfConversing();
            };
            audio.onended = finish;
            audio.onerror = finish;
            premiumAudioRef.current = audio;
            return audio.play();
          })
          .catch(() => {
            // Fall back to the browser voice so the reply is still spoken.
            premiumAudioRef.current = null;
            setSpeakingState(false);
            speakWithBrowserVoice();
          });
        return;
      }

      speakWithBrowserVoice();

      function speakWithBrowserVoice() {
        if (!ttsSupported) {
          resumeIfConversing();
          return;
        }
        window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      const voices = window.speechSynthesis.getVoices();
      const { uri: chosenUri, name: chosenName, rate: chosenRate } = preferredVoiceRef.current;
      if (typeof chosenRate === "number" && chosenRate > 0) {
        utterance.rate = Math.min(2, Math.max(0.5, chosenRate));
      }
      const preferred =
        (chosenUri ? voices.find((v) => v.voiceURI === chosenUri) : undefined) ||
        (chosenName ? voices.find((v) => v.name === chosenName) : undefined) ||
        voices.find((v) => v.lang.startsWith(navigator.language) && v.localService) ||
        voices.find((v) => v.lang.startsWith("en") && v.localService) ||
        voices.find((v) => v.lang.startsWith("en")) ||
        voices[0];
      if (preferred) utterance.voice = preferred;
      utterance.onstart = () => {
        setSpeakingState(true);
        // Keep the mic closed while Zeus talks so his own voice is never
        // transcribed as user input; it reopens when speech ends.
        recognitionRef.current?.abort();
      };
      utterance.onend = () => {
        setSpeakingState(false);
        resumeIfConversing();
      };
      utterance.onerror = () => {
        setSpeakingState(false);
        resumeIfConversing();
      };
      window.speechSynthesis.speak(utterance);
      }
    },
    [ttsSupported, startListening],
  );

  const stopSpeaking = useCallback(() => {
    if (ttsSupported) window.speechSynthesis.cancel();
    premiumAudioRef.current?.pause();
    premiumAudioRef.current = null;
    setSpeakingState(false);
  }, [ttsSupported]);

  useEffect(() => {
    return () => {
      // End conversation mode so any pending reopen-timeout closures from
      // this instance become no-ops (otherwise an unmounted copy of the
      // hook keeps restarting recognition and fights the live one).
      conversationModeRef.current = false;
      recognitionRef.current?.abort();
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      premiumAudioRef.current?.pause();
      premiumAudioRef.current = null;
    };
  }, []);

  return {
    sttSupported,
    ttsSupported,
    listening,
    speaking,
    conversationMode,
    voiceReplies,
    setVoiceReplies,
    startListening,
    stopListening,
    toggleListening,
    speakText,
    stopSpeaking,
  };
}
